<script setup>
import { storeToRefs } from 'pinia'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import DetailCard from '@/components/detail/DetailCard.vue'
import MapComponent from '@/components/map/MapComponent.vue'
import PredefinedValuesInputComponent from '@/components/PredefinedValuesInputComponent.vue'
import { useDetailMapHelpers } from '@/composables/useDetailMapHelpers'
import {
  DETAIL_EXTERNAL_LINKS,
  DETAIL_LANDBOARD_MAP_FEATURE_LAYER,
  LAYERS,
  MAP_CENTER,
  MAP_EXTENT,
  MAP_MINZOOM,
  MAP_PROJECTION,
} from '@/constants'
import { createGeoJsonFeature } from '@/services/map/FeatureService'
import { transform } from '@/services/map/ProjectionService'
import { formatLink } from '@/services/utilities'
import { useDetailStore } from '@/stores/useDetailStore'
import { useEsgStore } from '@/stores/useEsgStore'

const detailStore = useDetailStore()
const { landBoard } = storeToRefs(detailStore)

const esgMap = ref(null)

const props = defineProps({
  id: String,
  item: Object,
  cadasterId: { type: String, default: null },
  itemData: { type: Object, required: true },
})

const esgStore = useEsgStore()

const description = defineModel('description')
const evaluation = defineModel('evaluation')
const status = defineModel('status')

const { findLayer } = useDetailMapHelpers()

// Derived legend: base legends from config + dynamic legends from store (deduplicated)
const legend = computed(() => {
  const base = [...(props.item.legends || [])]
  const dynamic = esgStore.legends[props.id] || []
  const baseUrls = new Set(base)
  for (const url of dynamic) {
    if (!baseUrls.has(url)) base.push(url)
  }
  return base
})

// Self-cleaning watcher: clears stale legends when data resets (null), adds when API responds
watch(
  () => props.itemData?.fetched?.data?.layerName,
  newLayerName => {
    if (props.id !== 'mineral_deposit') return

    if (!newLayerName) {
      esgStore.legends[props.id] = []
      return
    }

    const legendUrl = `https://teenus.maaamet.ee/ows/maardlad?version=1.3.0&service=WMS&request=GetLegendGraphic&sld_version=1.1.0&layer=${newLayerName}&format=image/png&STYLE=default`
    if (!esgStore.legends[props.id]?.includes(legendUrl)) {
      esgStore.legends[props.id] = [...(esgStore.legends[props.id] || []), legendUrl]
    }
  },
  { immediate: true },
)

const mapview = ref({
  extent: transform('extent', MAP_EXTENT, 'EPSG:4326', MAP_PROJECTION),
  center: transform('fromLonLat', MAP_CENTER, MAP_PROJECTION),
  zoom: 3,
  minZoom: MAP_MINZOOM,
  maxZoom: props.id === 'radon_risk' ? 13 : 15,
  projection: MAP_PROJECTION,
  baseLayers: [],
  infoLayers: [],
  overlay: null,
  bbox: null,
})

const url = computed(() => {
  if (!props.item.link || !props.cadasterId) return null
  return formatLink(props.item.link, {
    cadasterId: props.cadasterId,
  })
})

watch(status, newStatus => {
  onToggleChange(newStatus)
})

onMounted(() => {
  if (props.item?.base_layers?.length) {
    mapview.value.baseLayers = props.item.base_layers.map(layer =>
      findLayer(layer),
    )
  }
  if (props.item?.layers?.length) {
    mapview.value.infoLayers = props.item.layers.map(layer => findLayer(layer))
  }
})

// Replace $onAction with a watch on landBoard
watch(
  landBoard,
  () => {
    if (status.value) {
      setMapData()
    }
  },
  { deep: true },
)

// Export auto-wait recapture: re-run the full capture path when this item is
// listed in the store's recapture request. Going through setMapData +
// MapComponent.exportMap (nextTick → animation settle → waitForMapLoaded)
// guarantees a freshly rendered frame, never a stale half-painted canvas.
watch(
  () => detailStore.esgRecaptureRequest,
  request => {
    if (!status.value || !request.keys.includes(props.id)) return
    setMapData()
    nextTick(() => {
      exportMap()
    })
  },
)

const onToggleChange = newStatus => {
  if (newStatus) {
    setMapData()

    nextTick(() => {
      exportMap()
    })
  }
}

const setMapData = () => {
  if (landBoard.value?.geometry) {
    createMapFeatures(landBoard.value.geometry)
    if (esgMap.value) {
      esgMap.value.updateOverlay(mapview.value.overlay)
    }
  }

  if (landBoard.value?.bbox) {
    mapview.value.bbox = landBoard.value.bbox
    if (esgMap.value) {
      esgMap.value.updateBbox(landBoard.value.bbox)
    }
  }
}

/* const findLayer = (id) => {
    const layer = cloneDeep(LAYERS.find((layer) => layer.id === id))
    if (layer) {
      layer.visible = true
      if (!layer.features) {
        layer.features = []
      }
    }
    return layer
  } */

const createMapFeatures = geometry => {
  const layer = findLayer(DETAIL_LANDBOARD_MAP_FEATURE_LAYER)
  if (!layer) {
    console.error(
      'Base layer for features not found:',
      DETAIL_LANDBOARD_MAP_FEATURE_LAYER,
    )
    return
  }
  layer.features = [createGeoJsonFeature(geometry, {})]
  mapview.value.overlay = layer
}

const exportMap = async () => {
  if (!esgMap.value || !status.value) {
    return
  }

  await nextTick()

  // Re-check after await — ref may have been cleared during Inertia page transition
  if (!esgMap.value) return

  try {
    const map = await esgMap.value.exportMap()

    detailStore.setMapExport({ key: props.id, value: map })
  } catch (error) {
    console.error('Error exporting map for ESG item:', props.id, error)

    detailStore.setMapExport({ key: props.id, value: '' })
  }
}
</script>

<template>
  <DetailCard :loading="itemData?.fetched?.loading ?? false" class="shadow-md rounded-lg">
    <template #heading>
      <a v-if="url" target="_blank" :href="url" class="hover:text-blue-600 hover:underline">
        {{ $t(`messages.detail.esg.${id}`) }}
      </a>
      <span v-else>
        {{ $t(`messages.detail.esg.${id}`) }}
      </span>
    </template>
    <template #controls>
      <label
        :for="`detail-esg-${id}-switch`"
        class="ml-auto mb-0 text-sm font-medium text-gray-600 dark:text-gray-400"
      >
        {{ $t(`messages.detail.esg.onoff`) }}
      </label>
      <ToggleSwitch :id="`detail-esg-${id}-switch`" v-model="status" />
    </template>
    <div class="p-4">
      <div class="mb-3">
        <PredefinedValuesInputComponent
          category="esg"
          :name="`${id}_description`"
          :placeholder="$t('messages.detail.esg.description')"
          v-model="description"
        />
      </div>
      <div v-if="status" class="esg-map border border-gray-300 dark:border-gray-600 rounded-sm overflow-hidden">
        <MapComponent
          :center="mapview.center"
          :extent="mapview.extent"
          :zoom="mapview.zoom"
          :bbox="mapview.bbox"
          :minZoom="mapview.minZoom"
          :maxZoom="mapview.maxZoom"
          :projection="mapview.projection"
          :baseLayers="mapview.baseLayers"
          :infoLayers="mapview.infoLayers"
          :overlay="mapview.overlay"
          :zoomToFeatures="true"
          :zoomControl="true"
          :enableGetFeatureInfo="!!item?.feature_info"
          :featureInfoServiceType="item?.feature_info"
          @update="exportMap"
          ref="esgMap"
        ></MapComponent>
        <div
          v-if="status && legend?.length"
          class="legend p-2 bg-white/80 shadow-sm rounded-sm absolute top-2 right-2 z-10 max-h-[calc(100%-1rem)] overflow-y-auto"
        >
          <div v-for="(entry, i) in legend" :key="`map-${id}-legend-${i}`">
            <img v-if="typeof entry === 'string'" :src="entry" alt="Map Legend" />
            <div v-else class="flex items-center gap-2 text-xs py-0.5">
              <span
                class="inline-block w-5 h-5 border border-gray-400"
                :style="{ backgroundColor: entry.color }"
              ></span>
              <span>{{ entry.label }}</span>
            </div>
          </div>
        </div>
      </div>
      <div v-if="status" class="mt-3">
        <PredefinedValuesInputComponent
          category="esg"
          :name="`${id}_evaluation`"
          :placeholder="$t('messages.detail.esg.evaluation')"
          v-model="evaluation"
        />
      </div>
    </div>
  </DetailCard>
</template>

<style scoped>
  .esg-map {
    position: relative;
    height: 450px;
    margin: 0;

    .legend {
      max-width: 180px;
    }
  }
</style>
